import React, {useState, useEffect, useRef} from 'react';
import {Set} from 'immutable';

interface SortableContainerProviderProps extends PropsWithChildrenRequired {
  onSortEnd: (
    orderedSortIndices: Set<number>,
    endIndex: number,
    updatedEndIndex: number,
    shouldCancel: boolean
  ) => void;
}

export type SortableContainerProviderExposedValues = {
  sortIndices: Set<number>;
  recentlyMovedIndices: number[] | null;
  isBulkMove: boolean;
  handleSingleSortStart: (e: React.MouseEvent<HTMLDivElement>, index: number) => void;
  handleMultiSortStart: (indexSet: Set<number>) => void;
  handleSortEnd: (
    e: React.MouseEvent<HTMLDivElement>,
    endIndex: number,
    shouldCancel?: boolean,
    overrideIndices?: Set<number>
  ) => void;
  hasSorted: boolean;
  setHasSorted: React.Dispatch<React.SetStateAction<boolean>>;
  cancelSortMode: () => void;
  handleMoveToTop: (e: React.MouseEvent<HTMLDivElement>, indexSet: Set<number>) => void;
};

export const SortableContainerContext = React.createContext<any>(null);

/*
  A good example of how this works with the `SortableElement` can be found in `SortableContainer.stories.js`
 */
export const SortableContainerProvider: React.FC<SortableContainerProviderProps> = ({
  children,
  onSortEnd,
  ...props
}) => {
  const [sortIndices, setSortIndices] = useState<Set<number>>(Set());
  const [recentlyMovedIndices, setRecentlyMovedIndices] = useState<number[] | null>(null);
  const [isBulkMove, setIsBulkMove] = useState(false);
  const [hasSorted, setHasSorted] = useState(false);

  // Refs (not render-scope locals) so the unmount cleanup sees the latest scheduled timeouts
  const recentlyMovedIndexTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const sortEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      clearTimeout(recentlyMovedIndexTimeoutRef.current);
      clearTimeout(sortEndTimeoutRef.current);
    };
  }, []);

  const cancelSortMode = () => {
    setSortIndices(Set());
  };

  const handleMultiSortStart = (indexSet: Set<number>) => {
    setSortIndices(indexSet);
  };

  const handleSingleSortStart = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    const newSortIndices = Set([index]);
    setSortIndices(newSortIndices);
    e.stopPropagation();
  };

  const handleMoveToTop = (e: React.MouseEvent<HTMLDivElement>, indexSet: Set<number>) => {
    setSortIndices(indexSet);
    setIsBulkMove(true);
    handleSortEnd(e, 0, undefined, indexSet);
  };

  const setSortState = (initialEndIndex: number, updatedEndIndex: number, overrideIndices?: Set<number>) => {
    const indices = overrideIndices ?? sortIndices;
    if (isBulkMove || !indices?.has(initialEndIndex)) {
      const updatedRecentlyMovedIndices = Array.from(
        {length: indices.count()},
        (_, i) => i + updatedEndIndex
      );
      setSortIndices(Set());
      setHasSorted(true);
      setIsBulkMove(false);
      setRecentlyMovedIndices(updatedRecentlyMovedIndices);
      recentlyMovedIndexTimeoutRef.current = setTimeout(() => setRecentlyMovedIndices(null), 2000);
    } else {
      setSortIndices(Set());
    }
  };

  const handleSortEnd = (
    e: React.MouseEvent<HTMLDivElement>,
    endIndex: number,
    shouldCancel?: boolean,
    overrideIndices?: Set<number>
  ) => {
    const effectiveIndices = overrideIndices ?? sortIndices;
    const orderedSortIndices = effectiveIndices
      .sort((setA, setB) => {
        return setB - setA;
      })
      .toSet();
    let updatedEndIndex = endIndex;
    let hasElementPastEndIndex = false;
    orderedSortIndices.forEach((index) => {
      if (index && index <= endIndex) {
        updatedEndIndex -= 1;
      } else {
        hasElementPastEndIndex = true;
      }
    });
    if (!hasElementPastEndIndex) {
      updatedEndIndex += 1;
    }
    onSortEnd(orderedSortIndices, endIndex, updatedEndIndex, shouldCancel || false);
    sortEndTimeoutRef.current = setTimeout(() => {
      setSortState(endIndex, updatedEndIndex, effectiveIndices);
    }, 0);
    e?.stopPropagation();
  };

  return (
    <SortableContainerContext.Provider
      value={{
        sortIndices,
        recentlyMovedIndices,
        isBulkMove,
        handleSingleSortStart,
        handleMultiSortStart,
        handleSortEnd,
        hasSorted,
        setHasSorted,
        cancelSortMode,
        handleMoveToTop,
        ...props
      }}
    >
      {children}
    </SortableContainerContext.Provider>
  );
};
